import React, { useEffect, useState, useRef } from 'react';
import { Search, BookOpen, Sparkles, Clock, X, Command } from 'lucide-react';
type SearchResult = {
  id: number;
  type: 'story' | 'storybook' | 'prompt';
  title: string;
  description: string;
  thumbnail?: string;
  date?: string;
};
type GlobalSearchProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectResult: (result: SearchResult) => void;
};
const mockResults: SearchResult[] = [{
  id: 1,
  type: 'story',
  title: "Grandma's Secret Recipe",
  description: 'Learning to make her famous apple pie from scratch',
  thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=100&h=100&fit=crop',
  date: 'Dec 15, 2024'
}, {
  id: 2,
  type: 'storybook',
  title: 'Achilles',
  description: 'A fascinating story about Achilles, a man destined for heroism',
  thumbnail: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=100&h=100&fit=crop',
  date: 'Dec 2, 2025'
}, {
  id: 3,
  type: 'story',
  title: 'Summer at the Lake House',
  description: 'Memories of fishing, swimming, and sunset bonfires',
  thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=100&h=100&fit=crop',
  date: 'Nov 20, 2024'
}];
export function GlobalSearch({
  isOpen,
  onClose,
  onSelectResult
}: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);
  useEffect(() => {
    if (query.trim()) {
      const filtered = mockResults.filter(r => r.title.toLowerCase().includes(query.toLowerCase()) || r.description.toLowerCase().includes(query.toLowerCase()));
      setResults(filtered);
      setSelectedIndex(0);
    } else {
      setResults([]);
    }
  }, [query]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        onSelectResult(results[selectedIndex]);
        onClose();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose, onSelectResult]);
  if (!isOpen) return null;
  const getIcon = (type: string) => {
    switch (type) {
      case 'story':
        return <BookOpen className="w-4 h-4" data-id="element-735" />;
      case 'storybook':
        return <Sparkles className="w-4 h-4" data-id="element-736" />;
      case 'prompt':
        return <Clock className="w-4 h-4" data-id="element-737" />;
      default:
        return <Search className="w-4 h-4" data-id="element-738" />;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]" data-id="element-739">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} data-id="element-740" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-741">
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200" data-id="element-742">
          <Search className="w-5 h-5 text-gray-400" data-id="element-743" />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search stories, storybooks, and more..." className="flex-1 text-lg outline-none" data-id="element-744" />
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-745">
            <X className="w-5 h-5 text-gray-400" data-id="element-746" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto" data-id="element-747">
          {query && results.length === 0 ? <div className="p-12 text-center text-gray-500" data-id="element-748">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-749" />
              <p data-id="element-750">No results found for "{query}"</p>
            </div> : results.length > 0 ? <div className="py-2" data-id="element-751">
              {results.map((result, index) => <button key={`${result.type}-${result.id}`} onClick={() => {
            onSelectResult(result);
            onClose();
          }} className={`w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors ${index === selectedIndex ? 'bg-gray-50' : ''}`} data-id="element-752">
                  {result.thumbnail && <img src={result.thumbnail} alt={result.title} className="w-12 h-12 rounded-lg object-cover" data-id="element-753" />}
                  <div className="flex-1 text-left" data-id="element-754">
                    <div className="flex items-center gap-2 mb-1" data-id="element-755">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${result.type === 'story' ? 'bg-green-100 text-green-700' : result.type === 'storybook' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-756">
                        {getIcon(result.type)}
                        {result.type}
                      </span>
                      {result.date && <span className="text-xs text-gray-500" data-id="element-757">
                          {result.date}
                        </span>}
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-758">
                      {result.title}
                    </div>
                    <div className="text-sm text-gray-600 line-clamp-1" data-id="element-759">
                      {result.description}
                    </div>
                  </div>
                </button>)}
            </div> : <div className="p-12 text-center text-gray-500" data-id="element-760">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-761">
                <Command className="w-8 h-8 text-gray-400" data-id="element-762" />
              </div>
              <p className="font-medium mb-2" data-id="element-763">Quick Search</p>
              <p className="text-sm" data-id="element-764">
                Search across all your stories and storybooks
              </p>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500" data-id="element-765">
          <div className="flex items-center gap-4" data-id="element-766">
            <span className="flex items-center gap-1" data-id="element-767">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded" data-id="element-768">
                ↑
              </kbd>
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded" data-id="element-769">
                ↓
              </kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1" data-id="element-770">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded" data-id="element-771">
                ↵
              </kbd>
              to select
            </span>
            <span className="flex items-center gap-1" data-id="element-772">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded" data-id="element-773">
                esc
              </kbd>
              to close
            </span>
          </div>
        </div>
      </div>
    </div>;
}